import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { VisualizationData } from '../../types';
import { formatNumber } from '../../lib/utils';

interface LineChartProps {
  data: VisualizationData;
  animated?: boolean;
}

const DEFAULT_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
];

export function LineChart({ data, animated }: LineChartProps) {
  const { config, data: chartData } = data;
  const { title, xAxis, yAxis, colors = DEFAULT_COLORS } = config;

  const xField = xAxis || (chartData[0] ? Object.keys(chartData[0])[0] : 'date');
  const yField = yAxis || (chartData[0] ? Object.keys(chartData[0])[1] : 'value');

  const numericFields = chartData[0]
    ? Object.keys(chartData[0]).filter(
        (key) => key !== xField && typeof chartData[0][key] === 'number'
      )
    : [yField];

  return (
    <div className="w-full">
      {title && (
        <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>
      )}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey={xField}
              tick={{ fontSize: 12 }}
              tickFormatter={(value) =>
                typeof value === 'string' && value.length > 10
                  ? value.substring(0, 10)
                  : value
              }
            />
            <YAxis
              tickFormatter={(value) => formatNumber(value)}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value, name) => {
                const numericValue =
                  typeof value === 'number' ? value : Number(value);
                const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
                return [formatNumber(safeValue), name];
              }}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            {numericFields.length > 1 && <Legend />}
            {numericFields.map((field, index) => (
              <Line
                key={field}
                type="monotone"
                dataKey={field}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ fill: colors[index % colors.length], r: 4 }}
                activeDot={{ r: 6 }}
                isAnimationActive={animated !== false}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
